import React from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/core/styles'
import { MATERIAL_SYMBOL_VIEWBOX, materialSymbolGlyphs } from './glyph-paths'

// Outline glyphs at SVG weight 300 render lighter than the former variable font
// at wght 300. A modest stroke restores the prior visual weight without
// switching glyph packages. Value was tuned on the coach sidebar against prod:
// 24 looked too heavy, 16 matched the former font at 20 / 24 / 35px sizes.
export const OUTLINE_STROKE_WIDTH = 16

const useStyles = makeStyles(theme => ({
  root: {
    // Match MUI SvgIcon layout so existing `& .MuiSvgIcon-root` rules keep working.
    userSelect: 'none',
    width: '1em',
    height: '1em',
    display: 'inline-block',
    flexShrink: 0,
    fontSize: 24,
    fill: 'currentColor',
    transition: theme.transitions.create('fill', {
      duration: theme.transitions.duration.shorter,
    }),
    verticalAlign: 'middle',
  },
  fontSizeInherit: { fontSize: 'inherit' },
  fontSizeSmall: { fontSize: 20 },
  fontSizeLarge: { fontSize: 35 },
  colorPrimary: { color: theme.palette.primary.main },
  colorSecondary: { color: theme.palette.secondary.main },
  colorAction: {
    color: (theme.palette.action && theme.palette.action.active) || undefined,
  },
  colorError: { color: theme.palette.error && theme.palette.error.main },
  colorDisabled: {
    color: (theme.palette.action && theme.palette.action.disabled) || undefined,
  },
}))

const resolvePath = (name, fill) => {
  const glyph = materialSymbolGlyphs[name]
  if (!glyph) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        `[MaterialSymbol] Unknown icon "${name}". Add a shim or EXTRA_NAMES entry and run npm run generate:material-symbol-paths.`,
      )
    }
    return null
  }
  return fill === 1 ? glyph.fill : glyph.outline
}

const MaterialSymbol = React.forwardRef(function MaterialSymbol(
  {
    name,
    children,
    fontSize = 'medium',
    color = 'inherit',
    // Kept for API compatibility with the former variable-font axes.
    // SVG glyphs are fixed at weight 300; only fill switches outline ↔ filled.
    weight: _weight = 300,
    fill = 0,
    grade: _grade = 0,
    opticalSize: _opticalSize,
    htmlColor,
    className,
    style,
    ...rest
  },
  ref,
) {
  const classes = useStyles()
  const symbolName = name || children
  const path =
    typeof symbolName === 'string' ? resolvePath(symbolName, fill) : null

  const numericFontSize = typeof fontSize === 'number' ? fontSize : null

  const hasAccessibleName = Boolean(
    rest['aria-label'] || rest['aria-labelledby'],
  )

  return (
    <svg
      ref={ref}
      viewBox={MATERIAL_SYMBOL_VIEWBOX}
      focusable="false"
      aria-hidden={hasAccessibleName ? undefined : 'true'}
      role={hasAccessibleName ? 'img' : undefined}
      className={clsx(
        classes.root,
        'MuiSvgIcon-root',
        'material-symbol',
        {
          [classes.fontSizeInherit]: fontSize === 'inherit',
          [classes.fontSizeSmall]: fontSize === 'small',
          [classes.fontSizeLarge]: fontSize === 'large',
          [classes.colorPrimary]: color === 'primary',
          [classes.colorSecondary]: color === 'secondary',
          [classes.colorAction]: color === 'action',
          [classes.colorError]: color === 'error',
          [classes.colorDisabled]: color === 'disabled',
          'MuiSvgIcon-fontSizeInherit': fontSize === 'inherit',
          'MuiSvgIcon-fontSizeSmall': fontSize === 'small',
          'MuiSvgIcon-fontSizeLarge': fontSize === 'large',
          'MuiSvgIcon-colorPrimary': color === 'primary',
          'MuiSvgIcon-colorSecondary': color === 'secondary',
          'MuiSvgIcon-colorAction': color === 'action',
          'MuiSvgIcon-colorError': color === 'error',
          'MuiSvgIcon-colorDisabled': color === 'disabled',
        },
        className,
      )}
      style={{
        ...(fill === 0 ? { overflow: 'visible' } : null),
        ...(numericFontSize !== null ? { fontSize: numericFontSize } : null),
        ...(htmlColor ? { color: htmlColor } : null),
        ...style,
      }}
      {...rest}
      data-icon={typeof symbolName === 'string' ? symbolName : undefined}
    >
      {path ? (
        <path
          d={path}
          {...(fill === 0
            ? {
              stroke: 'currentColor',
              strokeWidth: OUTLINE_STROKE_WIDTH,
              strokeLinejoin: 'round',
              strokeLinecap: 'round',
              paintOrder: 'stroke fill',
            }
            : null)}
        />
      ) : null}
    </svg>
  )
})

MaterialSymbol.propTypes = {
  name: PropTypes.string,
  children: PropTypes.node,
  fontSize: PropTypes.oneOfType([
    PropTypes.oneOf(['inherit', 'small', 'medium', 'default', 'large']),
    PropTypes.number,
  ]),
  color: PropTypes.oneOf([
    'inherit',
    'primary',
    'secondary',
    'action',
    'error',
    'disabled',
  ]),
  weight: PropTypes.number,
  fill: PropTypes.oneOf([0, 1]),
  grade: PropTypes.number,
  opticalSize: PropTypes.number,
  htmlColor: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
}

export default MaterialSymbol
